import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../shared/components/toast/useToast';
import { merchantOnboardingFieldsApi } from '../api/merchantOnboardingFieldsApi';
import type { MerchantOnboardingField, SetMerchantOnboardingFieldsPayload } from '../types';

const KEY = 'merchant-onboarding-fields';

/** Nest validation errors arrive as `message: string[]`; take the first line. */
const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

export const useMerchantOnboardingFields = (enabled = true) =>
  useQuery<MerchantOnboardingField[]>({
    queryKey: [KEY, 'list'],
    queryFn: merchantOnboardingFieldsApi.list,
    enabled,
  });

export const useSetMerchantOnboardingFields = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (payload: SetMerchantOnboardingFieldsPayload) =>
      merchantOnboardingFieldsApi.setMany(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY] });
      toast.success('Onboarding fields saved');
    },
    onError: (err: unknown) => {
      // ApiFetch already toasts the server message; only add one when it didn't get there.
      if (!(err as { toasted?: boolean })?.toasted) {
        toast.error(errMsg(err, 'Failed to save onboarding fields'));
      }
    },
  });
};
